import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from "react";
import { authMe, authLogin, authLogout, formatApiError, fetchPincodeInfo, fetchMyCart, replaceMyCart, mergeMyCart, fetchMyWishlist, replaceMyWishlist, fetchProduct, fetchUnreadCount, fetchLocationPulse, saveLanguagePref } from "@/lib/api";
import i18n from "@/i18n";
import { vendorOrderAlertsSeen } from "@/lib/vendorApi";
import { reloadIfContactPending } from "@/lib/contactGate";

const AppContext = createContext(null);

const LS = {
    pincode: "vb.pincode",
    cart: "vb.cart",
    wishlist: "vb.wishlist",
    mode: "vb.mode",
    radius: "vb.radiusKm",
    consent: "vb.locationConsent",
};

const load = (key, fallback) => {
    try {
        const v = localStorage.getItem(key);
        return v ? JSON.parse(v) : fallback;
    } catch {
        return fallback;
    }
};

// Snapshot the stored mode BEFORE React mounts and the first write-back
// useEffect runs. We use this to decide whether an authenticated vendor
// should auto-enter Vendor Mode (only when there is no persisted choice).
const _initialStoredMode = (() => {
    try { return localStorage.getItem("vb.mode"); } catch { return null; }
})();

// Browsing PIN is session-scoped (sessionStorage: survives refresh/navigation/screen lock, cleared when the tab closes).
// Saved PIN: { p: "110001", a: "Area" | null, u: userId | null }
try { localStorage.removeItem(LS.pincode); } catch { /* storage blocked: nothing persisted */ }
const loadPin = () => { try { const v = JSON.parse(sessionStorage.getItem(LS.pincode) || "null"); return v && /^\d{6}$/.test(v.p || "") ? v : null; } catch { return null; } };
const savePin = (v) => { try { v ? sessionStorage.setItem(LS.pincode, JSON.stringify(v)) : sessionStorage.removeItem(LS.pincode); } catch { /* storage blocked: PIN kept in memory only */ } };
const _initialPin = loadPin();

export const AppProvider = ({ children }) => {
    const [pincode, setPincodeState] = useState(() => _initialPin?.p || "");
    const [cart, setCart] = useState(() => load(LS.cart, []));
    const [wishlist, setWishlist] = useState(() => load(LS.wishlist, []));
    const [user, setUser] = useState(null);
    const [authChecking, setAuthChecking] = useState(true);
    const [mode, setModeState] = useState(() => load(LS.mode, "customer")); // "customer" | "vendor"
    const [radiusKm, setRadiusKmState] = useState(() => load(LS.radius, null)); // null = no filter
    const [locationConsent, setLocationConsentState] = useState(() => load(LS.consent, null)); // null | 'granted' | 'manual' | 'dismissed'

    useEffect(() => { localStorage.setItem(LS.cart, JSON.stringify(cart)); }, [cart]);

    // Push cart changes to the server (debounced) while the user is logged in.
    // We only start pushing once the cart has been hydrated from the server to
    // avoid overwriting a saved cart with the empty initial state on page load.
    useEffect(() => {
        if (!user || !cartHydratedRef.current) return;
        if (pushDebounceRef.current) clearTimeout(pushDebounceRef.current);
        const items = cart.map((c) => ({ id: c.id, qty: c.qty }));
        const key = JSON.stringify(items);
        if (key === lastCartSyncRef.current) return; // server already has this exact cart
        pushDebounceRef.current = setTimeout(() => {
            replaceMyCart(items).then(() => { lastCartSyncRef.current = key; }).catch(() => {});
        }, 400);
        return () => { if (pushDebounceRef.current) clearTimeout(pushDebounceRef.current); };
    }, [cart, user]);

    // Push wishlist changes to the server (debounced) once hydrated.
    useEffect(() => {
        if (!user || !wishlistHydratedRef.current) return;
        if (wishPushDebounceRef.current) clearTimeout(wishPushDebounceRef.current);
        const ids = wishlist.map((w) => w.id).filter(Boolean);
        const key = JSON.stringify(ids);
        if (key === lastWishSyncRef.current) return; // server already has this exact wishlist
        wishPushDebounceRef.current = setTimeout(() => {
            replaceMyWishlist(ids).then(() => { lastWishSyncRef.current = key; }).catch(() => {});
        }, 400);
        return () => { if (wishPushDebounceRef.current) clearTimeout(wishPushDebounceRef.current); };
    }, [wishlist, user]);
    useEffect(() => { localStorage.setItem(LS.wishlist, JSON.stringify(wishlist)); }, [wishlist]);
    useEffect(() => { localStorage.setItem(LS.mode, JSON.stringify(mode)); }, [mode]);
    useEffect(() => { localStorage.setItem(LS.radius, JSON.stringify(radiusKm)); }, [radiusKm]);
    useEffect(() => { localStorage.setItem(LS.consent, JSON.stringify(locationConsent)); }, [locationConsent]);

    // Cart server-sync coordination.
    // - cartHydrated flips to true after we load the user's server cart on login.
    //   Until then, we DO NOT push local changes to the server (prevents a brand-new
    //   session from wiping a saved cart with the empty guest cart).
    const cartHydratedRef = useRef(false);
    const pushDebounceRef = useRef(null);
    const wishlistHydratedRef = useRef(false);
    const wishPushDebounceRef = useRef(null);
    const lastCartSyncRef = useRef(null);
    const lastWishSyncRef = useRef(null);

    const hydrateCartFromServer = useCallback(async (guestItems) => {
        try {
            let data;
            if (guestItems && guestItems.length > 0) {
                // Merge guest items into the authenticated user's server cart.
                data = await mergeMyCart(guestItems.map((c) => ({ id: c.id, qty: c.qty })));
            } else {
                data = await fetchMyCart();
            }
            setCart(data.items || []);
            lastCartSyncRef.current = JSON.stringify((data.items || []).map((c) => ({ id: c.id, qty: c.qty })));
        } catch {
            // Network/API failure — keep the current (guest) cart as-is.
        } finally {
            cartHydratedRef.current = true;
        }
    }, []);

    const hydrateWishlistFromServer = useCallback(async (guestItems) => {
        try {
            const data = await fetchMyWishlist();
            // Wishlist is login-only, so the server list is the source of truth;
            // stale local entries (e.g. from another account) must not leak in.
            const map = {};
            (guestItems || []).forEach((w) => { if (w?.id) map[w.id] = w; });
            const ids = data.ids || [];
            setWishlist(ids.map((id) => map[id] || { id }));
            lastWishSyncRef.current = JSON.stringify(ids.filter(Boolean));
            const missing = ids.filter((id) => !map[id]?.name);
            if (missing.length) {
                // Only drop items whose product is truly gone (404); keep them on network errors.
                const details = await Promise.all(missing.map((id) => fetchProduct(id).catch((e) => (e?.response?.status === 404 ? { id, gone: true } : null))));
                const byId = Object.fromEntries(details.filter(Boolean).map((p) => [p.id, p]));
                setWishlist((prev) => prev.filter((w) => !byId[w.id]?.gone).map((w) => byId[w.id] || w));
            }
        } catch {
            // Network/API failure — keep local wishlist as-is.
        } finally {
            wishlistHydratedRef.current = true;
        }
    }, []);

    // Check existing session on mount
    useEffect(() => {
        let mounted = true;
        authMe()
            .then(async (res) => {
                if (!mounted) return;
                setUser(res.user);
                // If the user is a vendor and no mode choice has been persisted yet,
                // default to vendor mode so they immediately land in the vendor experience.
                const vendor = !!res.user?.roles?.includes("vendor");
                if (vendor && (_initialStoredMode == null || _initialStoredMode === "null")) {
                    setModeState("vendor");
                }
                // Load the authenticated user's server cart. If the browser already has
                // guest items in localStorage, merge them in (keeping the higher qty).
                const guest = (() => { try { return JSON.parse(localStorage.getItem(LS.cart) || "[]"); } catch { return []; } })();
                await hydrateCartFromServer(guest);
                // Hydrate the saved wishlist too so the heart persists across sessions.
                const guestWish = (() => { try { return JSON.parse(localStorage.getItem(LS.wishlist) || "[]"); } catch { return []; } })();
                await hydrateWishlistFromServer(guestWish);
            })
            .catch(() => { if (mounted) { setUser(null); setWishlist([]); } })
            .finally(() => { if (mounted) setAuthChecking(false); });
        return () => { mounted = false; };
    }, [hydrateCartFromServer]);

    const login = useCallback(async (email, password) => {
        const res = await authLogin({ email, password });
        setUser(res.user);
        // On fresh login, if the user has the vendor role, enter Vendor Mode by default.
        // The user can toggle back to Customer Mode via the header.
        if (res.user?.roles?.includes("vendor")) {
            setModeState("vendor");
        } else {
            setModeState("customer");
        }
        // Merge whatever is currently in the guest cart into the server cart.
        cartHydratedRef.current = false;
        wishlistHydratedRef.current = false;
        const guest = cart || [];
        const guestWish = wishlist || [];
        await hydrateCartFromServer(guest);
        await hydrateWishlistFromServer(guestWish);
        reloadIfContactPending();
        return res.user;
    }, [cart, wishlist, hydrateCartFromServer, hydrateWishlistFromServer]);

    // Called after OTP-verified signup completes (server already set auth cookies).
    const completeSignup = useCallback(async (signedUpUser) => {
        const res = { user: signedUpUser };
        setUser(res.user);
        setModeState("customer");
        cartHydratedRef.current = false;
        wishlistHydratedRef.current = false;
        const guest = cart || [];
        const guestWish = wishlist || [];
        await hydrateCartFromServer(guest);
        await hydrateWishlistFromServer(guestWish);
        reloadIfContactPending();
        return res.user;
    }, [cart, wishlist, hydrateCartFromServer, hydrateWishlistFromServer]);

    const logout = useCallback(async () => {
        // Cancel any in-flight debounced cart push so the outgoing user's items
        // never land in the next user's server cart.
        if (pushDebounceRef.current) { clearTimeout(pushDebounceRef.current); pushDebounceRef.current = null; }
        if (wishPushDebounceRef.current) { clearTimeout(wishPushDebounceRef.current); wishPushDebounceRef.current = null; }
        cartHydratedRef.current = false;
        wishlistHydratedRef.current = false;
        lastCartSyncRef.current = null;
        lastWishSyncRef.current = null;
        setCart([]);
        setWishlist([]);
        try { await authLogout(); } catch { /* network failure: local session is still cleared below */ }
        setUser(null);
        setModeState("customer");
        try { localStorage.removeItem(LS.mode); } catch { /* storage blocked: nothing to clear */ }
        savePin(null); setPincodeState(""); setPincodeArea(null);
    }, []);

    const refreshUser = useCallback(async () => {
        try {
            const res = await authMe();
            setUser(res.user);
            return res.user;
        } catch {
            setUser(null);
            return null;
        }
    }, []);

    const [pincodeArea, setPincodeArea] = useState(() => _initialPin?.a || null);
    const userIdRef = useRef(null);
    userIdRef.current = user?.id || null;
    const setPincode = (p, area = null) => {
        setPincodeState(p || ""); setPincodeArea(p ? area : null);
        savePin(p ? { p, a: area, u: userIdRef.current } : null);
    };
    // Saved PIN belongs to one account: clear it if a different user signs in; tag a guest PIN to the user.
    useEffect(() => {
        if (authChecking || !user) return;
        const v = loadPin();
        if (!v) return;
        if (v.u && v.u !== user.id) { savePin(null); setPincodeState(""); setPincodeArea(null); }
        else if (!v.u) savePin({ ...v, u: user.id });
    }, [user?.id, authChecking]); // eslint-disable-line react-hooks/exhaustive-deps
    // Profile PIN is never applied automatically; PinReminder offers it as a one-tap suggestion.
    // Drop a restored PIN that is no longer serviceable.
    useEffect(() => {
        if (!_initialPin) return;
        fetchPincodeInfo(_initialPin.p).catch((e) => {
            if (e?.response?.status === 404 && loadPin()?.p === _initialPin.p) { savePin(null); setPincodeState(""); setPincodeArea(null); }
        });
    }, []);

    const setMode = (m) => setModeState(m);

    const setRadiusKm = (r) => setRadiusKmState(r);
    const setLocationConsent = (c) => setLocationConsentState(c);

    const isVendor = !!user?.roles?.includes("vendor");
    const isAdmin = !!user?.roles?.includes("admin");
    const effectiveMode = isVendor ? mode : "customer";

    // Order alerts removed from UI (VerB is discovery-only); backend order data is untouched.
    const [orderAlertCount, setOrderAlertCount] = useState(0); // eslint-disable-line no-unused-vars
    const markOrdersSeen = useCallback(() => {
        setOrderAlertCount(0);
        vendorOrderAlertsSeen().catch(() => {});
    }, []);

    // ---- Cart & Wishlist (login required) ----
    const _requireAuth = (action) => {
        if (!user) {
            import("sonner").then(({ toast }) => toast.error(`Please login to ${action}.`));
            return false;
        }
        return true;
    };
    const addToCart = (product, qty = 1) => {
        if (!_requireAuth("add items to cart")) return false;
        setCart((prev) => {
            const existing = prev.find((c) => c.id === product.id);
            if (existing) return prev.map((c) => c.id === product.id ? { ...c, qty: c.qty + qty } : c);
            return [...prev, { id: product.id, name: product.name, image: product.image, price: product.price, mrp: product.mrp, unit: product.unit, shop_name: product.shop_name, qty }];
        });
        return true;
    };
    const updateCartQty = (id, qty) => {
        if (qty <= 0) return setCart((prev) => prev.filter((c) => c.id !== id));
        setCart((prev) => prev.map((c) => c.id === id ? { ...c, qty } : c));
    };
    const removeFromCart = (id) => setCart((prev) => prev.filter((c) => c.id !== id));
    const clearCart = () => setCart([]);

    const toggleWishlist = (product) => {
        if (!_requireAuth("save to wishlist")) return false;
        setWishlist((prev) => {
            const exists = prev.find((w) => w.id === product.id);
            if (exists) return prev.filter((w) => w.id !== product.id);
            return [...prev, { id: product.id, name: product.name, image: product.image, price: product.price, mrp: product.mrp, unit: product.unit, shop_name: product.shop_name }];
        });
        return true;
    };
    const inWishlist = (id) => wishlist.some((w) => w.id === id);

    const [unreadCount, setUnreadCount] = useState(0);
    const refreshUnread = useCallback(() => {
        if (!user) { setUnreadCount(0); return; }
        fetchUnreadCount(pincode).then((d) => setUnreadCount(d?.unread || 0)).catch(() => {});
    }, [user?.id, pincode]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        refreshUnread();
        if (!user) return undefined;
        const id = setInterval(refreshUnread, 60000);
        return () => clearInterval(id);
    }, [refreshUnread]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fast location alerts: cheap 10s signature poll (visible tab only); on change refresh bell + notify location widgets.
    useEffect(() => {
        if (!user) return undefined;
        let last = null;
        const tick = () => {
            if (document.visibilityState !== "visible" || navigator.onLine === false) return;
            fetchLocationPulse().then(({ sig }) => {
                if (last !== null && sig !== last) { refreshUnread(); window.dispatchEvent(new Event("vb:location-pulse")); }
                last = sig;
            }).catch(() => {});
        };
        tick();
        const id = setInterval(tick, 10000);
        return () => clearInterval(id);
    }, [user?.id, refreshUnread]); // eslint-disable-line react-hooks/exhaustive-deps

    // Language preference: account value wins on login; local choice is saved to the account.
    useEffect(() => {
        if (!user) return undefined;
        if (user.language && user.language !== i18n.language) i18n.changeLanguage(user.language);
        else if (!user.language && localStorage.getItem("vb.lang")) saveLanguagePref(i18n.language).catch(() => {});
        const onChange = (lng) => saveLanguagePref(lng).catch(() => {});
        i18n.on("languageChanged", onChange);
        return () => i18n.off("languageChanged", onChange);
    }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    const cartTotal = useMemo(() => cart.reduce((sum, c) => sum + c.price * c.qty, 0), [cart]);
    const cartCount = useMemo(() => cart.reduce((sum, c) => sum + c.qty, 0), [cart]);

    const value = {
        pincode, setPincode, pincodeArea,
        radiusKm, setRadiusKm,
        locationConsent, setLocationConsent,
        cart, addToCart, updateCartQty, removeFromCart, clearCart, cartTotal, cartCount,
        wishlist, toggleWishlist, inWishlist,
        user, setUser, authChecking, login, completeSignup, logout, refreshUser,
        isVendor, isAdmin, mode: effectiveMode, setMode,
        orderAlertCount, markOrdersSeen,
        unreadCount, refreshUnread,
        formatApiError,
    };
    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
    const ctx = useContext(AppContext);
    if (!ctx) throw new Error("useApp must be used within AppProvider");
    return ctx;
};
